// src/domains/core/dashboard/store/dashboardStore.ts

import type { WidgetConfig, WidgetMetadata } from "../types/widget.types";

import { create } from "zustand";
import { persist, devtools } from "zustand/middleware";

import { widgetRegistry, initializeWidgetRegistry } from "./widgetRegistry";

/**
 * Dashboard Store State
 */
interface DashboardState {
  // UI State
  isInitialized: boolean;

  // Widget Configurations
  widgets: WidgetConfig[];
}

/**
 * Dashboard Store Actions
 */
interface DashboardActions {
  getVisibleWidgets: () => WidgetConfig[];

  // Getters
  getWidget: (widgetId: string) => undefined | WidgetConfig;
  getWidgetsByCategory: (category: WidgetMetadata["category"]) => WidgetConfig[];
  // Initialization
  initialize: () => void;
  resetWidgets: () => void;
  setWidgetOrder: (widgetId: string, order: number) => void;

  setWidgetSize: (widgetId: string, size: WidgetConfig["size"]) => void;
  // Widget Management
  setWidgetVisible: (widgetId: string, visible: boolean) => void;
  updateWidgetConfig: (widgetId: string, config: Partial<WidgetConfig>) => void;
}

type DashboardStore = DashboardState & DashboardActions;

/**
 * Creates default widget configurations from registry
 *
 * @returns {WidgetConfig[]} Array of default widget configurations
 *
 */
function createDefaultWidgetConfigs(): WidgetConfig[] {
  // Ensure registry is initialized
  if (widgetRegistry.getAll().length === 0) {
    initializeWidgetRegistry();
  }

  const allWidgets = widgetRegistry.getAll();
  let order = 0;

  return allWidgets.map((metadata) => ({
    id: metadata.id,
    order: order++,
    size: metadata.defaultSize,
    type: metadata.id,
    visible: metadata.defaultVisible,
  }));
}

const initialState: DashboardState = {
  isInitialized: false,
  widgets: [],
};

/**
 * Dashboard Store
 *
 * Manages widget configurations with persistence
 */
export const useDashboardStore = create<DashboardStore>()(
  devtools(
    persist(
      (set, get) => ({
        ...initialState,

        /**
         * Get visible widgets sorted by order
         *
         * @returns {WidgetConfig[]} Array of visible widgets sorted by order
         *
         */
        getVisibleWidgets: () => {
          const visibleWidgets = get().widgets.filter((w) => w.visible);
          // Reason: toSorted() is preferred but TypeScript may not recognize it in all versions - using sort() on copy instead
          // eslint-disable-next-line unicorn/no-array-sort -- Reason: toSorted() not recognized by TypeScript
          return [...visibleWidgets].sort((a, b) => a.order - b.order);
        },

        /**
         * Get widget by ID
         *
         * @param widgetId - Widget identifier
         *
         * @returns {WidgetConfig | undefined} Widget configuration or undefined if not found
         *
         */
        getWidget: (widgetId) => {
          return get().widgets.find((w) => w.id === widgetId);
        },

        /**
         * Get widgets by category (only visible widgets)
         *
         * @param category - Widget category
         *
         * @returns {WidgetConfig[]} Array of visible widgets in category sorted by order
         *
         */
        getWidgetsByCategory: (category) => {
          const categoryWidgets = widgetRegistry.getByCategory(category);
          const categoryIds = new Set(categoryWidgets.map((w) => w.id));

          const filteredWidgets = get().widgets.filter((w) => categoryIds.has(w.id) && w.visible);
          // Reason: toSorted() is preferred but TypeScript may not recognize it in all versions - using sort() on copy instead
          // eslint-disable-next-line unicorn/no-array-sort -- Reason: toSorted() not recognized by TypeScript
          return [...filteredWidgets].sort((a, b) => a.order - b.order);
        },

        /**
         * Initialize dashboard with default widgets
         * Always syncs with registry to ensure all widgets exist and default visibility is respected
         */
        initialize: () => {
          const state = get();

          // Prevent infinite loops - only initialize once per session
          if (state.isInitialized) {
            return;
          }

          // Initialize registry if not already done
          if (widgetRegistry.getAll().length === 0) {
            initializeWidgetRegistry();
          }

          // If no widgets exist, create defaults
          if (state.widgets.length === 0) {
            const defaultWidgets = createDefaultWidgetConfigs();
            set({ isInitialized: true, widgets: defaultWidgets });
            return;
          }

          // Always sync existing widgets with registry
          // This ensures missing widgets are added and default visibility is respected
          const allRegistryWidgets = widgetRegistry.getAll();
          const existingWidgetIds = new Set(state.widgets.map((w) => w.id));

          // Find missing widgets
          const missingWidgets = allRegistryWidgets
            .filter((w) => !existingWidgetIds.has(w.id))
            .map((metadata, index) => ({
              id: metadata.id,
              order: state.widgets.length + index,
              size: metadata.defaultSize,
              type: metadata.id,
              visible: metadata.defaultVisible,
            }));

          // Update existing widgets: if they should be visible by default but aren't,
          // make them visible (one-time migration for existing users)
          // This handles the case where widgets were incorrectly initialized as hidden
          const updatedWidgets = state.widgets.map((widget) => {
            const registryWidget = allRegistryWidgets.find((rw) => rw.id === widget.id);
            if (registryWidget && registryWidget.defaultVisible && !widget.visible) {
              // Widget should be visible by default - make it visible
              // This is a one-time fix for existing installations
              return { ...widget, visible: true };
            }
            return widget;
          });

          // Combine updated and new widgets
          const mergedWidgets = [...updatedWidgets, ...missingWidgets];

          // Only update if there are changes
          const hasChanges =
            mergedWidgets.length !== state.widgets.length ||
            mergedWidgets.some((w, index) => {
              const existing = state.widgets[index];
              return !existing || w.visible !== existing.visible || w.id !== existing.id;
            });

          if (hasChanges) {
            set({ isInitialized: true, widgets: mergedWidgets });
          } else {
            set({ isInitialized: true });
          }
        },

        /**
         * Reset all widgets to defaults
         */
        resetWidgets: () => {
          const defaultWidgets = createDefaultWidgetConfigs();
          set({ widgets: defaultWidgets });
        },

        /**
         * Set widget order
         *
         * @param widgetId
         *
         * @param order
         *
         */
        setWidgetOrder: (widgetId, order) => {
          set((state) => ({
            widgets: state.widgets.map((widget) =>
              widget.id === widgetId ? { ...widget, order } : widget,
            ),
          }));
        },

        /**
         * Set widget size
         *
         * @param widgetId
         *
         * @param size
         *
         */
        setWidgetSize: (widgetId, size) => {
          set((state) => ({
            widgets: state.widgets.map((widget) =>
              widget.id === widgetId ? { ...widget, size } : widget,
            ),
          }));
        },

        /**
         * Set widget visibility
         *
         * @param widgetId
         *
         * @param visible
         *
         */
        setWidgetVisible: (widgetId, visible) => {
          set((state) => ({
            widgets: state.widgets.map((widget) =>
              widget.id === widgetId ? { ...widget, visible } : widget,
            ),
          }));
        },

        /**
         * Update widget configuration
         *
         * @param widgetId
         *
         * @param config
         *
         */
        updateWidgetConfig: (widgetId, config) => {
          set((state) => ({
            widgets: state.widgets.map((widget) =>
              widget.id === widgetId ? { ...widget, ...config } : widget,
            ),
          }));
        },
      }),
      {
        name: "tracq-dashboard-store",
        partialize: (state) => ({
          // Don't persist isInitialized - always check on load
          widgets: state.widgets,
        }),
      },
    ),
    {
      name: "DashboardStore",
    },
  ),
);
